import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import styles from "./FavouriteCard.module.scss";
import {
  EDIT_FAVOURITES_VIEW,
  ODDS_FILTER_LABELS,
  ODDS_FILTER_OPTIONS,
} from "../../../../constants";
import { EDIT, FAVOURITES, OPEN } from "../../../../constants/analytics";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";

const FavouriteCard = ({ favourite }) => {
  const dispatch = useDispatch();
  // Destructure all needed values from redux in one go for clarity
  const { ppConfig } = useSelector((state) => state.filter);
  const { format } = useSelector((state) => state.betslip);
  const labelMap = ODDS_FILTER_LABELS[format.toLowerCase()];
  const { marketGroups } = ppConfig;

  const { name, odds, markets = [] } = favourite;

  // Memoize derived values for performance
  const marketString = useMemo(() => {
    // Flatten all market groups to a single array

    const marketsArray = marketGroups.flatMap((group) => group.markets);
    // Filter and join market names that match the favourite's markets
    const names = marketsArray
      .filter(({ type }) => markets.includes(type))
      .map(({ name }) => name);
    // Remove duplicates using Set, then join
    return [...new Set(names)].join(", ");
  }, [marketGroups, markets]);

  const oddsLabel = useMemo(() => {
    if (!odds) return null;
    // Find the odds filter option matching the favourite's odds
    const options = ODDS_FILTER_OPTIONS.filter((opt) => {
      if (opt.minOdds >= odds?.minOdds && opt.maxOdds <= odds?.maxOdds) {
        return true;
      }
      return false;
    });

    if (options.length === 0) return null;
    if (options.length === 1) {
      return options[0][format.toLowerCase()].label;
    }

    const lowestOptionLabel = labelMap.get(options[0].minOdds ?? 0);
    const highestOptionLabel = labelMap.get(
      options[options.length - 1].maxOdds ?? Infinity
    );

    // // Return the label for the current odds format, if available
    return `${lowestOptionLabel} - ${highestOptionLabel}`;
  }, [odds, format]);

  // Compose the description string
  const description =
    oddsLabel && marketString
      ? `${oddsLabel}, ${marketString}`
      : oddsLabel || marketString || "";

  function handleEdit() {
    dispatch({
      type: "EDIT_FAVOURITE",
      payload: { ...favourite },
    });

    dispatch({
      type: "SET_ACTIVE_VIEW",
      payload: EDIT_FAVOURITES_VIEW,
    });

    sendAnalyticsEvent({
      event: FAVOURITES,
      type: EDIT,
      action: OPEN,
    });
  }

  return (
    <li className={styles.card}>
      <div className={styles.detailsCol}>
        <h4 className={styles.title}>{name}</h4>
        {description !== "" && (
          <p data-testid="description" className={styles.details}>
            {description}
          </p>
        )}
      </div>
      <button type="button" className={styles.editButton} onClick={handleEdit}>
        Edit
      </button>
    </li>
  );
};

FavouriteCard.propTypes = {
  favourite: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    odds: PropTypes.shape({
      minOdds: PropTypes.number,
      maxOdds: PropTypes.number,
      isDecimal: PropTypes.bool,
    }),
    markets: PropTypes.arrayOf(PropTypes.string.isRequired),
  }).isRequired,
};

export default FavouriteCard;
